import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS
import { map, Observable } from 'rxjs';

// Environments
import { environment } from '../../../environments/environment';

// Models
import { PaginatedDocumentResponse, Pagination } from '../../shared/models/pagination.model';
import { ContextDocument } from '../models/document.model';
import { DeleteFilesBulkResponse } from '../models/delete.model';


@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  constructor(private readonly http: HttpClient) { }

  getContextDocumentsList(contextId: string, pagination: Pagination, searchTerm: string) {
    let params = new HttpParams()
      .set('page', (pagination.page - 1).toString()) // Backend page is 0-based
      .set('pageSize', pagination.pageSize.toString())
      .set('sortColumn', pagination.sortColumn)
      .set('sortDirection', pagination.sortDirection)

    if (searchTerm) {
      params = params.set('name', searchTerm);
    }

    return this.http.get<PaginatedDocumentResponse<ContextDocument>>(`${environment.apiGatewayUrl}/docmanager/api/v1/documents/${contextId}`, { params })
      .pipe(
        map(response => ({
          ...response,
          documents: response.documents.map(document => ({
            id: document.id,
            name: document.name,
            contentType: document.contentType,
            size: document.size,
            user: document.userInfo.name ? document.userInfo.name.charAt(0) + '. ' + document.userInfo.surname : 'Utente Eliminato', // Trasformazione
            bucketName: document.bucketName,
            status: document.status,
            createTime: document.createTime,
            proxyUrl: document.proxyUrl,
            reason: document.reason
          }))
        }))
      );
  }

  uploadFile(file: File, contextId: string, overwrite: boolean = false): Observable<{ status: number, progress: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('contextId', contextId);
    formData.append('overwrite', overwrite.toString());

    return this.http.post(`${environment.apiGatewayUrl}/docmanager/api/v1/documents`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress: {
            // Calculate and return upload progress percentage
            const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
            return { status: 0, progress }; // Status 0 indicates that the upload is still in progress
          }
          case HttpEventType.Response:
            // Only return 100% if the response status is 200
            return { status: event.status, progress: event.status === 200 ? 100 : 0 };

          default:
            return { status: 0, progress: 0 };
        }
      })
    );
  }

  deleteFile(documentId: string) {
    return this.http.delete<undefined>(`${environment.apiGatewayUrl}/docmanager/api/v1/documents/${documentId}`);
  }

  deleteFilesBulk(documentIds: string[]) {
    return this.http.request<DeleteFilesBulkResponse>('DELETE', `${environment.apiGatewayUrl}/docmanager/api/v1/documents`, {
      body: {
        ids: documentIds
      }
    });
  }

  downloadDocument(documentId: String): Observable<HttpResponse<Blob>> {
    const url = `${environment.apiGatewayUrl}/docmanager/api/v1/documents/preview?documentId=${documentId}`;
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    });
  }
}
